import React, { useState, useEffect } from "react";
import {
  Grid,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  TextField,
  InputAdornment,
  Typography,
  IconButton,
  Box,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import { Refresh } from '@material-ui/icons';
import { withStyles } from '@material-ui/core/styles';
import moment from 'moment';

// Material-UI styles for the integration filters layout
const styles = (theme) => ({
  // Layout styles for the main filter container
  mainContainer: {
    display: "flex",
    width: "100%",
    alignItems: "end",
    flexWrap: "nowrap",
    marginTop: "1rem",
  },
  // Layout for the left side search section
  searchBySection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
    justifyContent: "flex-start",
  },
  // Layout for the right side actions section
  actionsSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
    justifyContent: "flex-start",
  },
  // Typography styles for filter labels
  filterLabel: {
    fontWeight: 550,
    whiteSpace: "nowrap",
    marginBottom: "1rem",
  },
  // Container for search input and button
  searchInputContainer: {
    width: "100%",
    display: "flex",
    alignItems: "center",
  },
  // Styling for the criteria dropdown
  selectField: {
    border: "1px solid black",
    borderRight: "none",
    borderRadius: "0.1rem",
    backgroundColor: "#F0F0F0",
  },
  // Styling for the search text input
  searchInput: {
    flex: 1,
    minWidth: 0,
    border: "1px solid black",
    borderTopRightRadius: "8px",
    borderBottomRightRadius: "8px",
    borderTopLeftRadius: "0px",
    borderBottomLeftRadius: "0px",
  },
  // Orange search button styling
  searchButton: {
    backgroundColor: "#EB5E28",
    color: "white",
    borderTopRightRadius: "7px",
    borderBottomRightRadius: "7px",
    borderTopLeftRadius: "0px",
    borderBottomLeftRadius: "0px",
    padding: "8px",
    translate: "0.84rem",
    marginLeft: "0px",
    marginRight: "0px",
  },
  // Styling for per page selector (right aligned)
  perPageContainer: {
    display: "flex",
    alignItems: "center",
    marginLeft: "auto",
  },
  perPageLabel: {
    marginRight: "0.5rem",
    fontSize: "0.875rem",
  },
  perPageSelect: {
    minWidth: 60,
  },
});

const IntegrationFilters = ({ classes, onFilterChange, filters, rowsPerPage, onRowsPerPageChange, lastSyncTime, onRefresh, onSearch, onClearFilters }) => {
  // Local state to manage filter values before applying them
  const [localFilters, setLocalFilters] = useState(filters);

  // Sync localFilters with props when filters change externally (like clear filters)
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Process filter updates and notify parent component
  const handleFilterChange = (field, value) => {
    let newFilters = { ...localFilters, [field]: value };

    // Clear searchKeyword when criteria changes
    if (field === 'criteria') {
      newFilters = { ...newFilters, searchKeyword: '' };
    }

    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  // Clears the search keyword input
  const handleClearSearch = () => {
    handleFilterChange('searchKeyword', '');
  };

  return (
    <>
      <Grid
        container
        style={{
          display: "flex",
          width: "100%",
          alignItems: "end",
          flexWrap: "nowrap",
          marginTop: "1rem",
          justifyContent: "space-between",
        }}
      >
        <Grid
          item
          xs={12}
          sm={8}
          md={8}
          style={{
            display: "flex",
            justifyContent: "flex-start",
            gap: "1rem",
          }}
        >
          <Grid
            item
            style={{flexGrow: 1, flexBasis: '440px', maxWidth: '440px', minWidth: '440px'}}
            // xs={9}
            // sm={9}
            // md={9}
          >
            <Typography
              style={{
                fontWeight: 550,
                whiteSpace: "nowrap",
                marginBottom: "1rem",
              }}
              className="dashboard-filter-labels"
            >
              View By:
            </Typography>
            <Box
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
              }}
              className="select-search-by-field"
            >
              <TextField
                size="small"
                select
                value={localFilters.criteria}
                onChange={(e) => handleFilterChange('criteria', e.target.value)}
                variant="outlined"
                style={{
                  border: "1px solid black",
                  borderRight: "none",
                  borderRadius: "0.1rem",
                  backgroundColor: "#F0F0F0",
                }}
                className="select-search"
              >
                <MenuItem value="all">Select Criteria</MenuItem>
                <MenuItem value="integration_name">Integration Name</MenuItem>
                <MenuItem value="job_name">Job Name</MenuItem>
                <MenuItem value="vendor_plant">Vendor Plant</MenuItem>
                <MenuItem value="vendor_number">Vendor Number</MenuItem>
                <MenuItem value="status">Status</MenuItem>
              </TextField>

              {localFilters.criteria === 'status' ? (
                <div style={{ display: 'flex', flex: 1 }}>
                  <TextField
                    size="small"
                    select
                    value={localFilters.searchKeyword}
                    onChange={(e) => handleFilterChange('searchKeyword', e.target.value)}
                    variant="outlined"
                    placeholder="Select Status..."
                    disabled={localFilters.criteria === 'all'}
                    displayEmpty
                    style={{
                      flex: 1,
                      minWidth: 0,
                      border: "1px solid black",
                      borderRight: "none",
                      borderTopRightRadius: "0px",
                      borderBottomRightRadius: "0px",
                      borderTopLeftRadius: "0px",
                      borderBottomLeftRadius: "0px",
                    }}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (selected) => {
                        if (!selected) {
                          return <span style={{ color: '#999' }}>Select Status</span>;
                        }
                        return selected === 'success' ? 'Successful' :
                               selected === 'failed' ? 'Failed' :
                               selected === 'running' ? 'Running' :
                               selected === 'retrying' ? 'Retrying' : selected;
                      }
                    }}
                  >
                    <MenuItem value="">Select Status</MenuItem>
                    <MenuItem value="success">Successful</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                    <MenuItem value="running">Running</MenuItem>
                    <MenuItem value="retrying">Retrying</MenuItem>
                  </TextField>
                  <IconButton
                    size="small"
                    onClick={onSearch}
                    disabled={localFilters.criteria === 'all' || !localFilters.searchKeyword}
                    style={{
                      backgroundColor: (localFilters.criteria === 'all' || !localFilters.searchKeyword) ? "#ccc" : "#EB5E28",
                      color: "white",
                      borderTopRightRadius: "7px",
                      borderBottomRightRadius: "7px",
                      borderTopLeftRadius: "0px",
                      borderBottomLeftRadius: "0px",
                      padding: "8px",
                      border: "1px solid black",
                      borderLeft: "none",
                      cursor: (localFilters.criteria === 'all' || !localFilters.searchKeyword) ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <SearchIcon />
                  </IconButton>
                </div>
              ) : (
                <TextField
                  size="small"
                  value={localFilters.searchKeyword}
                  onChange={(e) =>
                    handleFilterChange("searchKeyword", e.target.value)
                  }
                  onBlur={(e) => {
                    const trimmed = e.target.value.trim();
                    if (trimmed !== e.target.value) {
                      handleFilterChange("searchKeyword", trimmed);
                    }
                  }}
                  variant="outlined"
                  disabled={localFilters.criteria === 'all'}
                  placeholder={localFilters.criteria === 'all' ? 'Select criteria' :
                    localFilters.criteria === 'integration_name' ? 'Enter integration name...' :
                    localFilters.criteria === 'job_name' ? 'Enter job name...' :
                    localFilters.criteria === 'vendor_plant' ? 'Enter vendor plant...' :
                    localFilters.criteria === 'vendor_number' ? 'Enter vendor number...' :
                    'Search keyword...'}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    border: "1px solid black",
                    borderTopRightRadius: "8px",
                    borderBottomRightRadius: "8px",
                    borderTopLeftRadius: "0px",
                    borderBottomLeftRadius: "0px",
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {localFilters.searchKeyword && localFilters.criteria !== 'all' && (
                            <IconButton
                              size="small"
                              onClick={handleClearSearch}
                              style={{
                                padding: "4px",
                                marginRight: "4px",
                              color: "#666"
                              }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          )}
                        <IconButton
                          size="small"
                          onClick={onSearch}
                          disabled={localFilters.criteria === 'all'}
                          style={{
                            backgroundColor: localFilters.criteria === 'all' ? "#ccc" : "#EB5E28",
                            color: "white",
                            borderTopRightRadius: "7px",
                            borderBottomRightRadius: "7px",
                            borderTopLeftRadius: "0px",
                            borderBottomLeftRadius: "0px",
                            padding: "8px",
                            translate: "0.84rem",
                            marginLeft: "0px",
                            marginRight: "0px",
                            cursor: localFilters.criteria === 'all' ? 'not-allowed' : 'pointer',
                          }}
                        >
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </Box>
          </Grid>
          <Grid
            item
            style={{ display: "flex", gap: "8px", marginTop: "31px", width: "100%", alignItems: "center" }}
            // xs={6}
            // sm={6}
            // md={6}
          >
            {/* {(localFilters.searchKeyword || localFilters.criteria !== 'all' || localFilters.integrationName || localFilters.jobName || localFilters.vendorPlant || localFilters.vendorNumber) && ( */}
            <Typography
              variant="caption"
              style={{
                fontSize: 14,
                color: "#EB5E28",
                textDecoration: "underline",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
              onClick={onClearFilters}
              title="Clear All Filters"
            >
              Clear Filters
            </Typography>
            {/* )} */}
          </Grid>
        </Grid>

        <Grid item style={{ width: "40%", marginLeft: "2rem" }}>
          <Grid
            container
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "end",
            }}
          >
            <Grid
              item
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <Grid>
                <Typography
                  variant="body2"
                  style={{
                    color: "gray",
                    marginRight: "8px",
                    whiteSpace: "nowrap",
                  }}
                  className="last-synced-text"
                >
                  {`Last synced @ ${moment(lastSyncTime).format(
                    "DD-MMM-YYYY, HH:mm:ss"
                  )}`}
                </Typography>
              </Grid>
              <Grid>
                <Typography
                  variant="body2"
                  color="primary"
                  style={{
                    textDecoration: "underline",
                    fontStyle: "italic",
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  className="refresh-button"
                  onClick={onRefresh}
                >
                  Refresh
                  <Refresh color="primary" fontSize="small" className="refresh-button"/>
                </Typography>
              </Grid>
            </Grid>

            <Grid
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                marginTop: "10px",
                width: "100%",
              }}
              className="per-page-box"
            >
              <Typography
                style={{
                  fontWeight: "bold",
                  marginRight: "4px",
                  whiteSpace: "nowrap",
                }}
              >
                PER PAGE
              </Typography>
              <FormControl variant="outlined" size="small">
                <Select
                  value={rowsPerPage}
                  onChange={onRowsPerPageChange}
                >
                  {/* <MenuItem value={5}>5</MenuItem> */}
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default withStyles(styles)(IntegrationFilters);
